/**
 * 筋功能控制器（主线程编排中枢）。
 *
 * 设计说明：
 * 1. 本控制器只负责“进入/退出筋、吸收默认实体选择、预览与提交编排”；
 * 2. 鼠标事件与外观沿用 Viewer 默认实体选择链路，不自定义拾取与叠加层；
 * 3. 真正的几何求解由 Viewer.commitFeature / Viewer.executeFeaturePreview 走 worker 完成，
 *    主线程禁止直接 import rib-kernel 或 getPuppyKernel；
 * 4. 轮廓 profile 由草图子系统准备并经 setProfile 注入，本控制器不采集草图几何。
 */

import * as THREE from 'three/webgpu';
import type { Subscription } from 'rxjs';
import type { Viewer } from '../../viewer';
import {
    EntityInteractionEventType,
    type SelectionUpdatedEventPayload,
} from '../../manager/EntitySelectionManager';
import { convertMmToM, convertMToMm } from '../../utils/unit';
import { resolveTopologySelectionPick } from '../../utils/feature';
import { buildProfileCurveSegmentsFromSketchObject } from '../../utils/feature/profileCurveSegments';
import { isSketchGroup } from '../../mesh';
import { buildRibModelTopologyBindings } from '../binding/buildRibModelTopologyBindings';
import { RibFeatureDefinitionModel } from '../definition/rib/RibFeatureDefinition';

/** 默认筋厚度（mm）。 */
const DEFAULT_RIB_THICKNESS_MM = 2;

/**
 * 筋功能控制器。
 */
export class RibFeatureController {
    /** Viewer 实例。 */
    private readonly viewer: Viewer;
    /** 选择事件订阅集合。 */
    private readonly subscriptions: Subscription[] = [];
    /** 状态变化监听器集合（供宿主响应式刷新）。 */
    private readonly stateListeners = new Set<() => void>();
    /** 是否处于筋交互态。 */
    private active = false;
    /** 编辑态特征 ID；新建为 null。 */
    private editingFeatureId: string | null = null;
    /** 第一侧厚度（底层米制）。 */
    private thickness = convertMmToM(DEFAULT_RIB_THICKNESS_MM);
    /** 第二侧厚度（底层米制）；twoSides 时有效。 */
    private secondThickness = convertMmToM(DEFAULT_RIB_THICKNESS_MM);
    /** 厚度类型；singleSide 暂不受内核支持。 */
    private thicknessType: RibThicknessType = 'centered';
    /** 厚度方向来源。 */
    private directionMode: RibDirectionMode = 'sketchNormal';
    /** directionMode 为 explicit 时的方向。 */
    private explicitDirection: RibPoint3 | null = null;
    /** 是否反转材料侧。 */
    private reverseMaterialSide = false;
    /** 操作类型：rib 加料 / groove 减料。 */
    private operation: 'rib' | 'groove' = 'rib';
    /** 是否允许轮廓沿支撑面滑动。 */
    private enableSliding = true;
    /** 已注入的轮廓输入。 */
    private profile: RibProfileInput | null = null;
    /** 已选中的上游实体对象 ID。 */
    private selectedBodyObjectId: string | null = null;
    /** 抑制选择管理器同步回放的选择事件。 */
    private suppressSelectionReplay = false;
    /** 最近一次筋预览失败的用户提示；成功或清除后为 null。 */
    private previewError: string | null = null;
    /** 最近一次预览失败对应的输入签名，用于避免相同输入反复重试。 */
    private lastPreviewFailureKey: string | null = null;
    /** 进入筋前的选择模式。 */
    private selectionModeBeforeRib: 'object' | 'face' | 'edge' | 'vertex' | 'face-edge' | 'topology' = 'object';

    /**
     * @param viewer Viewer 实例。
     */
    public constructor(viewer: Viewer) {
        this.viewer = viewer;
    }

    /**
     * 是否处于筋交互态。
     *
     * @returns 交互态。
     */
    public get isActive(): boolean {
        return this.active;
    }

    /**
     * 订阅控制器状态变化。
     *
     * @param listener 状态变化监听器。
     * @returns 取消订阅函数。
     */
    public subscribe(listener: () => void): () => void {
        this.stateListeners.add(listener);
        return () => {
            this.stateListeners.delete(listener);
        };
    }

    /**
     * 通知宿主控制器状态已变化。
     */
    private notifyStateChanged(): void {
        for (const listener of this.stateListeners) {
            listener();
        }
    }

    /**
     * 进入筋功能。
     *
     * 设计说明：筋吸收“上游实体”，进入时必须把底层拾取约束为 object 模式，
     * 否则默认选择链路选不到完整实体。
     */
    public enterRibFeature(): void {
        if (this.active) {
            return;
        }
        this.active = true;
        this.editingFeatureId = null;
        this.selectedBodyObjectId = null;
        this.profile = null;
        this.previewError = null;
        this.lastPreviewFailureKey = null;
        this.selectionModeBeforeRib = this.viewer.getSelectionMode();
        this.viewer.setSelectionMode('object', { preserveSelection: true });
        this.bindSelectionEvents();
        this.notifyStateChanged();
    }

    /**
     * 退出筋功能。
     */
    public exitRibFeature(): void {
        if (!this.active) {
            return;
        }
        this.active = false;
        this.unbindSelectionEvents();
        this.selectedBodyObjectId = null;
        this.profile = null;
        this.previewError = null;
        this.lastPreviewFailureKey = null;
        this.viewer.setSelectionMode(this.selectionModeBeforeRib, { preserveSelection: true });
        this.viewer.clearFeaturePreviewRequests();
        void this.viewer.setExtrudePreviewMesh(null);
        this.notifyStateChanged();
    }

    /**
     * 注入筋轮廓输入。
     *
     * @param profile 轮廓输入；传 null 清除。
     */
    public setProfile(profile: RibProfileInput | null): void {
        this.profile = profile;
        this.lastPreviewFailureKey = null;
        this.notifyStateChanged();
        void this.previewRib();
    }

    /**
     * 获取当前轮廓输入。
     *
     * @returns 轮廓输入；未注入时为 null。
     */
    public getProfile(): RibProfileInput | null {
        return this.profile ? structuredClone(this.profile) : null;
    }

    /**
     * 从当前场景选择中吸收草图轮廓。
     *
     * 设计说明：
     * 1. 筋与拉伸/旋转同属 ProfileDriven 特征，复用 Viewer 既有的草图轮廓解析链路；
     * 2. 这里只把解析到的首个闭合轮廓投影成筋的 points + plane，不重新实现草图构线；
     * 3. 解析失败（未选中草图轮廓 / 开放草图）时返回 false，由宿主决定提示方式。
     *
     * @returns 是否成功吸收轮廓。
     */
    public absorbSelectedSketchProfile(): boolean {
        const objects = this.viewer.entitySelectionManager?.getSelectedObjects() ?? [];
        for (const object of objects) {
            const objectId = (object as THREE.Object3D & { objectId?: string }).objectId
                ?? (object as { uuid?: string }).uuid;
            if (typeof objectId !== 'string' || objectId.length === 0) {
                continue;
            }
            const resolution = this.viewer.resolveBossExtrudeSelectionContours(objectId);
            if (!Array.isArray(resolution) || resolution.length === 0) {
                continue;
            }
            this.setProfile(this.buildRibProfileFromContour(resolution[0]!));
            return true;
        }
        // 闭合轮廓解析失败时，回退到开放草图轮廓（与 SolidWorks 允许开放筋草图对齐）。
        return this.absorbOpenSketchProfile(objects);
    }

    /**
     * 从开放草图对象（直线/圆弧）吸收筋轮廓。
     *
     * @param objects 当前选中对象。
     * @returns 是否成功吸收。
     */
    private absorbOpenSketchProfile(objects: THREE.Object3D[]): boolean {
        for (const object of objects) {
            const group = this.resolveParentSketchGroup(object);
            const plane = (group as unknown as {
                referencePlane?: {
                    normal?: THREE.Vector3;
                    xAxis?: THREE.Vector3;
                    yAxis?: THREE.Vector3;
                    localToWorld?: (point: THREE.Vector2) => THREE.Vector3;
                };
            })?.referencePlane;
            if (!group || !plane?.normal) {
                continue;
            }
            const points = this.sampleSketchObjectPoints(object);
            if (points.length < 2) {
                continue;
            }
            const origin = points[0]!;
            const segments = buildProfileCurveSegmentsFromSketchObject(object);
            this.setProfile({
                points,
                ...(segments.length > 0 ? { segments } : {}),
                plane: {
                    origin: [...origin],
                    // 反转参考面法向：BRepFeat_MakeLinearForm 用平面与线框方向判定材料侧，
                    // 当前正法向会把材料生成到实体的反方向，取反后材料侧朝向所选实体。
                    normal: [-plane.normal.x, -plane.normal.y, -plane.normal.z],
                    xAxis: [plane.xAxis.x, plane.xAxis.y, plane.xAxis.z],
                    yAxis: [plane.yAxis.x, plane.yAxis.y, plane.yAxis.z],
                },
            });
            return true;
        }
        return false;
    }

    /**
     * 采样草图对象的世界点列。
     *
     * 设计说明：
     * 1. 直线/曲线草图对象都维护一份用于渲染的采样几何（BufferGeometry），
     *    直接读取其 position 属性即可得到世界坐标点列，直线为 2 点、曲线为 N 点；
     * 2. 若对象没有采样几何，再回退到 startPoint/endPoint 的局部点转世界坐标；
     * 3. 首尾重合时丢弃最后一点，保证开放线框不会在端点处闭合。
     *
     * @param object 草图对象。
     * @returns 世界坐标点列。
     */
    private sampleSketchObjectPoints(object: THREE.Object3D): RibPoint3[] {
        const geometry = (object as THREE.Object3D & {
            geometry?: THREE.BufferGeometry & {
                getAttribute?: (name: string) => THREE.BufferAttribute | undefined;
            };
        }).geometry;
        const position = typeof geometry?.getAttribute === 'function'
            ? geometry.getAttribute('position')
            : undefined;
        if (position && position.count >= 2) {
            const points: RibPoint3[] = [];
            for (let index = 0; index < position.count; index += 1) {
                points.push([position.getX(index), position.getY(index), position.getZ(index)]);
            }
            const first = points[0]!;
            const last = points[points.length - 1]!;
            const closed = Math.hypot(
                first[0] - last[0],
                first[1] - last[1],
                first[2] - last[2],
            ) < 1e-9;
            return closed ? points.slice(0, -1) : points;
        }

        const line = object as unknown as {
            startPoint?: { x: number; y: number; z: number | null };
            endPoint?: { x: number; y: number; z: number | null };
        };
        if (!line.startPoint || !line.endPoint) {
            return [];
        }
        const group = this.resolveParentSketchGroup(object);
        const plane = (group as unknown as {
            referencePlane?: { localToWorld?: (point: THREE.Vector2) => THREE.Vector3 };
        })?.referencePlane;
        const toWorld = (point: { x: number; y: number; z: number | null }): RibPoint3 => {
            if (typeof plane?.localToWorld === 'function') {
                const world = plane.localToWorld(new THREE.Vector2(point.x, point.y));
                return [world.x, world.y, world.z];
            }
            return [point.x, point.y, point.z ?? 0];
        };
        return [toWorld(line.startPoint), toWorld(line.endPoint)];
    }

    /**
     * 沿父链查找所属草图组。
     *
     * @param object 场景对象。
     * @returns 草图组；未找到时返回 null。
     */
    private resolveParentSketchGroup(object: THREE.Object3D): THREE.Object3D | null {
        let current: THREE.Object3D | null = object;
        while (current) {
            if (isSketchGroup(current)) {
                return current;
            }
            current = current.parent;
        }
        return null;
    }

    /**
     * 把场景轮廓投影成筋轮廓输入。
     *
     * @param contour 场景轮廓。
     * @returns 筋轮廓输入。
     */
    private buildRibProfileFromContour(contour: {
        geometry: {
            outer: Array<[number, number, number]>;
            normal: [number, number, number];
            xAxis: [number, number, number];
            yAxis: [number, number, number];
        };
        basePoint: { x: number; y: number; z: number };
    }): RibProfileInput {
        return {
            points: contour.geometry.outer.map((point) => [...point] as [number, number, number]),
            plane: {
                origin: [contour.basePoint.x, contour.basePoint.y, contour.basePoint.z],
                normal: [...contour.geometry.normal] as [number, number, number],
                xAxis: [...contour.geometry.xAxis] as [number, number, number],
                yAxis: [...contour.geometry.yAxis] as [number, number, number],
            },
        };
    }

    /**
     * 获取当前第一侧厚度（mm）。
     *
     * @returns 第一侧厚度（mm）。
     */
    public getThicknessMm(): number {
        return convertMToMm(this.thickness);
    }

    /**
     * 获取当前第二侧厚度（mm）。
     *
     * @returns 第二侧厚度（mm）。
     */
    public getSecondThicknessMm(): number {
        return convertMToMm(this.secondThickness);
    }

    /**
     * 获取当前厚度类型。
     *
     * @returns 厚度类型。
     */
    public getThicknessType(): RibThicknessType {
        return this.thicknessType;
    }

    /**
     * 获取当前厚度方向来源。
     *
     * @returns 方向来源。
     */
    public getDirectionMode(): RibDirectionMode {
        return this.directionMode;
    }

    /**
     * 获取当前操作类型。
     *
     * @returns 操作类型。
     */
    public getOperation(): 'rib' | 'groove' {
        return this.operation;
    }

    /**
     * 判断当前筋是否允许提交。
     *
     * @returns 可提交标记。
     */
    public getRibCanSubmit(): boolean {
        if (!this.active || this.thickness <= 0) {
            return false;
        }
        if (typeof this.selectedBodyObjectId !== 'string' || this.selectedBodyObjectId.length === 0) {
            return false;
        }
        return !!this.profile;
    }

    /**
     * 获取当前已吸收的上游实体对象 ID。
     *
     * @returns 上游实体对象 ID；未选中时为 null。
     */
    public getSelectedBodyObjectId(): string | null {
        return this.selectedBodyObjectId;
    }

    /**
     * 获取最近一次筋预览失败的用户提示。
     *
     * @returns 预览失败提示；无失败时为 null。
     */
    public getRibPreviewError(): string | null {
        return this.previewError;
    }

    /**
     * 清除筋预览失败提示（由 UI 在弹窗确认后调用）。
     */
    public clearRibPreviewError(): void {
        if (this.previewError === null) {
            return;
        }
        this.previewError = null;
        this.notifyStateChanged();
    }

    /**
     * 获取面向属性面板的筋定义投影。
     *
     * @returns 筋定义快照。
     */
    public getRibDefinition(): RibFeatureDefinition | null {
        if (!this.profile) {
            return null;
        }
        const definition: RibFeatureDefinition = {
            profile: structuredClone(this.profile),
            thicknessType: this.thicknessType,
            thickness: this.thickness,
            directionMode: this.directionMode,
            operation: this.operation,
            enableSliding: this.enableSliding,
            reverseMaterialSide: this.reverseMaterialSide,
            tolerance: 1e-6,
        };
        if (this.thicknessType === 'twoSides') {
            definition.secondThickness = this.secondThickness;
        }
        if (this.directionMode === 'explicit' && this.explicitDirection) {
            definition.explicitDirection = [...this.explicitDirection];
        }
        return definition;
    }

    /**
     * 设置第一侧厚度（mm）并触发预览。
     *
     * @param mm 第一侧厚度（mm）。
     */
    public setThicknessMm(mm: number): void {
        if (!Number.isFinite(mm) || mm <= 0) {
            return;
        }
        this.thickness = convertMmToM(mm);
        this.notifyStateChanged();
        void this.previewRib();
    }

    /**
     * 设置第二侧厚度（mm）并触发预览。
     *
     * @param mm 第二侧厚度（mm）。
     */
    public setSecondThicknessMm(mm: number): void {
        if (!Number.isFinite(mm) || mm <= 0) {
            return;
        }
        this.secondThickness = convertMmToM(mm);
        this.notifyStateChanged();
        void this.previewRib();
    }

    /**
     * 设置厚度类型并触发预览。
     *
     * @param thicknessType 厚度类型。
     */
    public setThicknessType(thicknessType: RibThicknessType): void {
        this.thicknessType = thicknessType;
        this.notifyStateChanged();
        void this.previewRib();
    }

    /**
     * 设置厚度方向来源并触发预览。
     *
     * @param directionMode 方向来源。
     */
    public setDirectionMode(directionMode: RibDirectionMode): void {
        this.directionMode = directionMode;
        this.notifyStateChanged();
        void this.previewRib();
    }

    /**
     * 设置显式厚度方向并触发预览。
     *
     * @param direction 单位方向。
     */
    public setExplicitDirection(direction: RibPoint3 | null): void {
        this.explicitDirection = direction ? [...direction] : null;
        this.notifyStateChanged();
        void this.previewRib();
    }

    /**
     * 设置是否反转材料侧并触发预览。
     *
     * @param reversed 是否反转。
     */
    public setReverseMaterialSide(reversed: boolean): void {
        this.reverseMaterialSide = reversed;
        this.notifyStateChanged();
        void this.previewRib();
    }

    /**
     * 设置操作类型并触发预览。
     *
     * @param operation 操作类型。
     */
    public setOperation(operation: 'rib' | 'groove'): void {
        this.operation = operation;
        this.notifyStateChanged();
        void this.previewRib();
    }

    /**
     * 设置是否允许滑动并触发预览。
     *
     * @param enabled 是否允许滑动。
     */
    public setEnableSliding(enabled: boolean): void {
        this.enableSliding = enabled;
        this.notifyStateChanged();
        void this.previewRib();
    }

    /**
     * 预览筋。
     */
    public async previewRib(): Promise<void> {
        if (!this.active) {
            return;
        }
        if (!this.selectedBodyObjectId || !this.profile) {
            await this.viewer.setExtrudePreviewMesh(null);
            return;
        }

        const inputKey = this.buildPreviewInputKey();
        // 同一输入上一次已求解失败时跳过，避免用户每点击一次都重复触发失败与提示。
        if (this.lastPreviewFailureKey === inputKey) {
            return;
        }

        const { definitionModel, bindings } = this.buildRibInputs();
        try {
            const result = await this.viewer.executeFeaturePreview({
                featureKind: 'rib',
                featureId: this.editingFeatureId,
                definition: definitionModel,
                bindings,
            });
            if (result.meshData) {
                await this.viewer.setExtrudePreviewMesh(result.meshData as never, {
                    prototype: result.prototype,
                    draftDefinition: result.draftDefinition,
                });
            }
            this.lastPreviewFailureKey = null;
            if (this.previewError !== null) {
                this.previewError = null;
                this.notifyStateChanged();
            }
        } catch (error) {
            await this.viewer.setExtrudePreviewMesh(null);
            this.lastPreviewFailureKey = inputKey;
            this.previewError = '当前选中实体/轮廓不支持筋';
            this.clearFailedPreviewSelection();
            this.notifyStateChanged();
        }
    }

    /**
     * 构建当前筋预览输入的签名。
     *
     * 设计说明：签名覆盖实体、轮廓、厚度与操作，用于判断两次预览请求是否等价，
     * 避免选择事件高频重试同一失败输入。
     *
     * @returns 输入签名。
     */
    private buildPreviewInputKey(): string {
        const profileKey = this.profile
            ? this.profile.points.map((point) => point.join(',')).join(';')
            : '';
        return [
            this.selectedBodyObjectId ?? '',
            this.thickness,
            this.secondThickness,
            this.thicknessType,
            this.directionMode,
            this.operation,
            profileKey,
        ].join(':');
    }

    /**
     * 获取面向宿主的筋提交载荷。
     *
     * @returns 提交载荷。
     */
    public getRibCommitPayload(): {
        featureId: string | null;
        definitionModel: RibFeatureDefinitionModel;
        bindings: ReturnType<typeof buildRibModelTopologyBindings>;
        upstreamBodyObjectId: string | null;
    } {
        const { definitionModel, bindings } = this.buildRibInputs();
        return {
            featureId: this.editingFeatureId,
            definitionModel,
            bindings,
            upstreamBodyObjectId: this.selectedBodyObjectId,
        };
    }

    /**
     * 执行筋（✔ 提交）。
     */
    public async executeRib(): Promise<void> {
        if (!this.active) {
            return;
        }
        if (!this.selectedBodyObjectId || !this.profile) {
            throw new Error('筋至少需要一个上游实体与一个轮廓');
        }
        const upstreamBodyObjectId = this.selectedBodyObjectId;
        const { definitionModel, bindings } = this.buildRibInputs();
        const result = await this.viewer.commitFeature({
            featureKind: 'rib',
            featureId: this.editingFeatureId,
            definition: definitionModel,
            bindings,
        });

        // 新建筋成功后，把正式结果体网格替换到场景：新增结果体、移除旧实体与残留预览。
        if (!result.replaced && result.meshData) {
            await this.viewer.addResultMesh(result.meshData as never, {
                prototype: result.prototype,
            });
            if (upstreamBodyObjectId && result.objectId && upstreamBodyObjectId !== result.objectId) {
                this.viewer.removeResultMesh(upstreamBodyObjectId);
                this.viewer.removeSceneObject(upstreamBodyObjectId);
            }
        }
        await this.viewer.setExtrudePreviewMesh(null);

        this.exitRibFeature();
    }

    /**
     * 绑定默认选择事件。
     */
    private bindSelectionEvents(): void {
        if (this.subscriptions.length > 0) {
            return;
        }
        const selectionManager = this.viewer.entitySelectionManager;
        if (!selectionManager) {
            return;
        }
        this.subscriptions.push(
            selectionManager
                .observe(EntityInteractionEventType.SelectionUpdated)
                .subscribe(this.handleSelectionUpdated),
        );
    }

    /**
     * 解绑选择事件。
     */
    private unbindSelectionEvents(): void {
        this.subscriptions.forEach((subscription) => subscription.unsubscribe());
        this.subscriptions.length = 0;
    }

    /**
     * 选择变化回调：同步选中的实体。
     */
    private readonly handleSelectionUpdated = (payload: SelectionUpdatedEventPayload): void => {
        if (!this.active || this.suppressSelectionReplay) {
            return;
        }
        const clickedObjects = payload.selectionSourceObjects ?? payload.selectedObjects ?? [];
        // 点中实体时吸收为上游基体；点中草图对象时尝试吸收为筋轮廓。
        // 实体选中效果由 EntitySelectionManager 在 object 模式下自动整实体高亮。
        const hasSolidCandidate = clickedObjects.some((object) => (
            typeof (object as THREE.Object3D & { objectId?: string }).objectId === 'string'
        ));
        if (hasSolidCandidate) {
            this.syncSelectedBody();
        } else {
            this.absorbSelectedSketchProfile();
        }
        this.notifyStateChanged();
        void this.previewRib();
    };

    /**
     * 把当前默认选中的对象投影为上游实体。
     */
    private syncSelectedBody(): void {
        const objects = this.viewer.entitySelectionManager?.getSelectedObjects() ?? [];
        // 整实体点击：对象自身携带 objectId。
        const selectedBody = objects.find((object) => (
            typeof (object as THREE.Object3D & { objectId?: string }).objectId === 'string'
        ));
        if (selectedBody) {
            this.selectedBodyObjectId = (selectedBody as THREE.Object3D & { objectId: string }).objectId;
            return;
        }
        // 面/拓扑命中：通过拓扑选择拾取解析所属实体，保证“选中实体的面”也能吸收为筋基体。
        for (const object of objects) {
            const pick = resolveTopologySelectionPick(object);
            if (pick && typeof pick.bodyObjectId === 'string' && pick.bodyObjectId.length > 0) {
                this.selectedBodyObjectId = pick.bodyObjectId;
                this.replaceSelectionWithBody(pick.bodyObjectId);
                return;
            }
        }
        this.selectedBodyObjectId = null;
    }

    /**
     * 把当前选择替换为整实体选择，使“点面”呈现“整实体选中”效果。
     *
     * @param bodyObjectId 所属实体对象 ID。
     */
    private replaceSelectionWithBody(bodyObjectId: string): void {
        const manager = this.viewer.entitySelectionManager;
        const scene = this.viewer.getScene();
        if (!manager || !scene) {
            return;
        }
        let bodyObject: THREE.Object3D | null = null;
        scene.traverse((candidate) => {
            if (bodyObject) {
                return;
            }
            const candidateObjectId = (candidate as THREE.Object3D & { objectId?: string }).objectId;
            if (candidateObjectId === bodyObjectId || candidate.uuid === bodyObjectId) {
                bodyObject = candidate;
            }
        });
        if (!bodyObject) {
            return;
        }
        // 抑制回放，避免替换选择再次触发 handleSelectionUpdated 造成递归。
        this.suppressSelectionReplay = true;
        try {
            // 选择条目必须带 point/distance 才能被选择中心视为完整拾取，仅给 object 会保持旧的高亮。
            manager.updateSelectionState([{
                object: bodyObject,
                distance: 0,
                point: bodyObject.getWorldPosition(new THREE.Vector3()),
            }]);
        } finally {
            queueMicrotask(() => {
                this.suppressSelectionReplay = false;
            });
        }
    }

    /**
     * 预览失败时自动取消当前实体选择。
     *
     * 设计说明：失败的实体/轮廓不支持筋，保留选中会诱导用户反复点击同一目标。
     */
    private clearFailedPreviewSelection(): void {
        this.selectedBodyObjectId = null;
        const manager = this.viewer.entitySelectionManager;
        if (!manager) {
            return;
        }
        this.suppressSelectionReplay = true;
        try {
            manager.updateSelectionState([]);
        } finally {
            queueMicrotask(() => {
                this.suppressSelectionReplay = false;
            });
        }
    }

    /**
     * 组装筋定义模型与持久化绑定。
     *
     * @returns 定义模型与绑定。
     */
    private buildRibInputs(): {
        definitionModel: RibFeatureDefinitionModel;
        bindings: ReturnType<typeof buildRibModelTopologyBindings>;
    } {
        const definition = this.getRibDefinition();
        if (!definition) {
            throw new Error('筋轮廓尚未注入，无法组装定义');
        }
        const upstreamBodyObjectId = this.selectedBodyObjectId;
        const definitionModel = RibFeatureDefinitionModel.fromParams({
            commandParams: {
                featureKind: 'rib',
                upstreamBodyObjectId: upstreamBodyObjectId ?? undefined,
                definition,
            },
        });
        const bindings = buildRibModelTopologyBindings({
            upstreamBodyObjectId,
            definition,
        });
        return { definitionModel, bindings };
    }
}
